import { DashboardIcon } from "@/assets/images/sidebar-icons/dashboard"
import { PaymentIcon } from "@/assets/images/sidebar-icons/payment"
import { MessagesIcon } from "@/assets/images/svgIcons/messagesIcon"
import type { MenuItem } from "@/components/ui/layout/menuSidebar/type-sidebar"
import { type BoxDetailsEntity } from "@modules/box/entities/boxEntity"
import { word } from "@modules/core/utils/i18n"
import { getBoxPath } from "@modules/navigation/hooks/useBoxNavigate"
import { Path } from "@modules/navigation/routes"
import { Box, CalendarDays, Dot, Settings, StoreIcon, UserRound } from "lucide-react"

export type BoxMenuConfig = {
  box: BoxDetailsEntity | null
  hasUnreadMessage: boolean
}

export const createBoxMenuModel = (config: BoxMenuConfig): MenuItem[] => {
  const { box, hasUnreadMessage } = config
  const permissions = box?.getPermissions()
  const boxId = box?.id
  const menu: MenuItem[] = []

  // Dashboard - Always visible
  const itemDashboard: MenuItem = {
    label: word("menu.dashboard"),
    icon: <DashboardIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Dashboard, { newBoxId: boxId }),
    relativeTo: [getBoxPath(Path.Box.Dashboard, { newBoxId: boxId })],
  }

  // Chat - Always visible
  const itemChat: MenuItem = {
    label: word("menu.chat"),
    icon: (
      <span className="relative inline-block">
        <MessagesIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />
        {hasUnreadMessage && (
          <span className="absolute -top-0.5 right-0 flex h-2 w-2 items-center justify-center">
            <Dot strokeWidth={20} color="currentColor" className="text-red-500" />
          </span>
        )}
      </span>
    ),
    to: getBoxPath(Path.Chat.Root, { newBoxId: boxId }),
    badge: hasUnreadMessage ? ("new" as "new" | "updated") : undefined,
    relativeTo: [getBoxPath(Path.Chat.Root, { newBoxId: boxId })],
  }

  // Planning - Requires SEE_TIMETABLES permission
  const itemPlanning: MenuItem = {
    label: word("menu.planning"),
    icon: <CalendarDays className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Planning, { newBoxId: boxId }),
    relativeTo: [
      getBoxPath(Path.Box.Planning, { newBoxId: boxId }),
      getBoxPath(Path.Box.ProgrammationDetails(":id"), { newBoxId: boxId }),
      getBoxPath(Path.Box.ProgrammationTemplate(":id"), { newBoxId: boxId }),
      getBoxPath(Path.Box.ProgrammationSessions(":id"), { newBoxId: boxId }),
    ],
  }

  // Offers - Requires SEE_OFFERS permission
  const itemOffers: MenuItem = {
    label: word("menu.offers"),
    icon: <Box className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Offers, { newBoxId: boxId }),
    relativeTo: [getBoxPath(Path.Box.Offers, { newBoxId: boxId })],
  }

  // Shop - Requires SEE_SHOP permission
  const itemShop: MenuItem = {
    label: word("menu.shop"),
    icon: <StoreIcon className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Shop, { newBoxId: boxId }),
    relativeTo: [getBoxPath(Path.Box.Shop, { newBoxId: boxId })],
  }

  // Members - Requires SEE_MEMBERS permission
  const itemMembers: MenuItem = {
    label: word("menu.members"),
    icon: <UserRound className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Members, { newBoxId: boxId }),
    relativeTo: [getBoxPath(Path.Box.Members, { newBoxId: boxId })],
  }

  // Finances - Requires SEE_FINANCIAL permission
  const itemFinances: MenuItem = {
    label: word("menu.finances"),
    icon: <PaymentIcon color="currentColor" className="w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Finances.Root, { newBoxId: boxId }),
    relativeTo: [getBoxPath(Path.Box.Finances.Root, { newBoxId: boxId })],
  }

  // Admin - Requires SEE_GYM_INFORMATION permission
  const itemAdmin: MenuItem = {
    label: word("menu.admin"),
    icon: <Settings className="currentColor w-(--sidebar-icon-width) min-w-(--sidebar-icon-width)" />,
    to: getBoxPath(Path.Box.Admin, { newBoxId: boxId }),
    relativeTo: [getBoxPath(Path.Box.Admin, { newBoxId: boxId })],
  }

  // Build menu in order
  menu.push(itemDashboard)

  if (permissions?.canSeeTimetables) {
    menu.push(itemPlanning)
  }

  if (permissions?.canSeeOffers) {
    menu.push(itemOffers)
  }

  if (permissions?.canSeeShop) {
    menu.push(itemShop)
  }

  if (permissions?.canSeeMembers) {
    menu.push(itemMembers)
  }

  menu.push(itemChat)

  if (permissions?.canSeeFinancial) {
    menu.push(itemFinances)
  }

  if (permissions?.canSeeGymInformation) {
    menu.push(itemAdmin)
  }

  return menu
}
